import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl px-4 py-2.5 text-sm outline-none transition-all";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1.5";

const EditProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState({ name: '', phone: '', city: '', state: '', gender: '', dob: '', current_team: '', position: '', height: '', bio: '', achievements: '' });

  useEffect(() => {
    axios.get(`${API}/profile`, { withCredentials: true }).then(({ data }) => {
      setProfile({
        name: data.name || '', phone: data.phone || '', city: data.city || '', state: data.state || '',
        gender: data.gender || '', dob: data.dob || '', current_team: data.current_team || '',
        position: data.position || '', height: data.height || '', bio: data.bio || '', achievements: data.achievements || ''
      });
    }).catch((e) => logError('Failed to load profile:', e));
  }, []);

  const onChange = (e) => setProfile({ ...profile, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setSaved(false);
    try {
      await axios.put(`${API}/profile`, profile, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      logError('Profile update failed:', err);
      window.alert('Failed to update profile');
    }
    finally { setLoading(false); }
  };

  const fields = ['name', 'phone', 'city', 'gender', 'dob', 'current_team', 'position', 'height'];
  const completion = Math.round((fields.filter(f => profile[f]?.trim?.()).length / fields.length) * 100);

  return (
    <>
      <SEO title="Edit Profile" description="Edit your Beyond Ball India player profile." path="/profile/edit" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-link">
            <ArrowLeft size={16} weight="bold" /> Back
          </button>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-2" data-testid="edit-profile-title">Edit profile</h1>
          <p className="text-gray-600 mb-6">Keep your details up to date.</p>

          <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
            <div className="flex justify-between text-xs mb-2">
              <span className="text-gray-500 font-semibold uppercase">Completion</span>
              <span className="font-bold text-[#F97316]">{completion}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#F97316] to-orange-400 transition-all" style={{width: `${completion}%`}}></div>
            </div>
          </div>

          {saved && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 mb-5 text-sm rounded-xl" data-testid="saved-message">Profile saved!</div>}

          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
            <div>
              <label className={labelCls}>Full name</label>
              <input type="text" name="name" value={profile.name} onChange={onChange} className={inputCls} data-testid="name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Phone</label><input type="tel" name="phone" value={profile.phone} onChange={onChange} className={inputCls} data-testid="phone-input" /></div>
              <div><label className={labelCls}>Date of birth</label><input type="date" name="dob" value={profile.dob} onChange={onChange} className={inputCls} data-testid="dob-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>City</label><input type="text" name="city" value={profile.city} onChange={onChange} className={inputCls} data-testid="city-input" /></div>
              <div><label className={labelCls}>State</label><input type="text" name="state" value={profile.state} onChange={onChange} className={inputCls} data-testid="state-input" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Gender</label>
                <select name="gender" value={profile.gender} onChange={onChange} className={inputCls} data-testid="gender-select">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Position</label>
                <select name="position" value={profile.position} onChange={onChange} className={inputCls} data-testid="position-select">
                  <option value="">Select</option><option>Point Guard</option><option>Shooting Guard</option><option>Small Forward</option><option>Power Forward</option><option>Center</option>
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1"><label className={labelCls}>Height</label><input type="text" name="height" placeholder='6&apos;2&quot;' value={profile.height} onChange={onChange} className={inputCls} data-testid="height-input" /></div>
            </div>
            <div><label className={labelCls}>Current team</label><input type="text" name="current_team" value={profile.current_team} onChange={onChange} className={inputCls} data-testid="team-input" /></div>
            <div><label className={labelCls}>Bio</label><textarea name="bio" value={profile.bio} onChange={onChange} rows={3} className={inputCls} data-testid="bio-input" /></div>
            <div><label className={labelCls}>Achievements</label><textarea name="achievements" value={profile.achievements} onChange={onChange} rows={3} className={inputCls} data-testid="achievements-input" /></div>
            <button type="submit" disabled={loading} className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-profile-btn">
              {loading ? 'Saving...' : 'Save profile'} {!loading && <FloppyDisk size={16} weight="bold" />}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
